/**
 * Shared video-style mode configuration — single source of truth for the
 * 4-mode picker that appears in BOTH StepIdea and StepSetup. Lives here so
 * label / caption / tooltip / icon edits don't have to be made in two
 * places (and silently drift apart). See Task 9 review for context.
 */
import { Clapperboard, Smartphone, Sun, Zap, type LucideIcon } from 'lucide-react'

import type { VideoMode } from '@/components/create/StepSetup'

export interface VideoModeConfig {
  value: VideoMode
  label: string
  /** Short one-line caption shown beneath the picker for the SELECTED mode. */
  caption: string
  /** Longer hover-tooltip description used on each per-button Tooltip. */
  tooltip: string
  Icon: LucideIcon
}

export const VIDEO_MODE_CONFIG: readonly VideoModeConfig[] = [
  {
    value: 'ugc',
    label: 'UGC',
    Icon: Smartphone,
    caption:
      'Real creator-with-phone footage. Handheld micro-shake, two mixed real-world light sources, off-center framing, sensor grain — the AI picks an authentic scenario (bedroom selfie, desk review, GRWM, unboxing) from your idea and stays in it.',
    tooltip:
      'Looks like footage shot on a real iPhone in a real home. Handheld, mixed lighting, sensor grain, no studio polish. Best for influencer-style ads, social-native creatives, and anything that needs to feel non-commercial.',
  },
  {
    value: 'cinematic_product',
    label: 'Cinematic',
    Icon: Clapperboard,
    caption: 'Polished product commercial. Dolly, rim light, shallow DoF.',
    tooltip:
      'Premium product commercial look: smooth dolly or orbit camera, controlled rim light, shallow depth of field, considered composition. Best for luxury / hero brand moments.',
  },
  {
    value: 'lifestyle',
    label: 'Lifestyle',
    Icon: Sun,
    caption: 'Natural light editorial. Organic human-centered framing.',
    tooltip:
      'Magazine-style editorial: natural window or golden-hour light, gentle push-in or rack focus, organic human moments. Best for skincare / wellness / home-goods storytelling.',
  },
  {
    value: 'hype_retail',
    label: 'Hype Retail',
    Icon: Zap,
    caption: 'Punchy DTC. Hard rim, colored gels, sharp cuts.',
    tooltip:
      'High-energy DTC retail look: hard rim light, one colored accent gel, sharp quick push-ins, dynamic cropped framing. Best for promo drops, sales, hype product launches.',
  },
] as const

/** Lookup helper for the selected mode's caption / tooltip / icon. */
export function getVideoModeConfig(mode: VideoMode): VideoModeConfig {
  return VIDEO_MODE_CONFIG.find((m) => m.value === mode) ?? VIDEO_MODE_CONFIG[0]
}
